import { ExploreResponse, ExploreFilters, PlaceItem } from '../types/explore';
import { API_BASE_URL } from '../config';

class ExploreService {
  private baseUrl = `${API_BASE_URL}/api/explore`;

  /**
   * Fetch places with pagination and filtering
   * @param filters - Filter options
   * @returns Promise with places data
   */
  async getPlaces(filters: ExploreFilters): Promise<ExploreResponse> {
    try {
      const params = new URLSearchParams();
      
      params.append('type', filters.type);
      params.append('limit', filters.limit.toString());
      
      if (filters.lastDoc) {
        params.append('lastDoc', filters.lastDoc);
      }
      
      if (filters.city) {
        params.append('city', filters.city);
      }
      
      if (filters.subtype) {
        params.append('subtype', filters.subtype);
      }
      
      if (filters.tags && filters.tags.length > 0) {
        params.append('tags', filters.tags.join(','));
      }
      
      if (filters.priceRange) {
        if (filters.priceRange.min !== undefined) {
          params.append('priceMin', filters.priceRange.min.toString());
        }
        if (filters.priceRange.max !== undefined) {
          params.append('priceMax', filters.priceRange.max.toString());
        }
      }

      // New filter parameters
      if (filters.minRating && filters.minRating !== 'any') {
        params.append('minRating', filters.minRating);
      }
      
      if (filters.hotelStar && filters.hotelStar !== 'any') {
        params.append('hotelStar', filters.hotelStar);
      }
      
      if (filters.selectedCuisines && filters.selectedCuisines.length > 0) {
        params.append('cuisines', filters.selectedCuisines.join(','));
      }
      
      if (filters.selectedSignatureDish && filters.selectedSignatureDish.length > 0) {
        params.append('signatureDishes', filters.selectedSignatureDish.join(','));
      }
      
      if (filters.selectedFoodTags && filters.selectedFoodTags.length > 0) {
        params.append('foodTags', filters.selectedFoodTags.join(','));
      }
      
      if (filters.foodPriceMin !== undefined) {
        params.append('foodPriceMin', filters.foodPriceMin.toString());
      }
      
      if (filters.foodPriceMax !== undefined) {
        params.append('foodPriceMax', filters.foodPriceMax.toString());
      }
      
      if (filters.bestTimeOfDay && filters.bestTimeOfDay.length > 0) {
        params.append('bestTimeOfDay', filters.bestTimeOfDay.join(','));
      }
      
      if (filters.bestSeason && filters.bestSeason.length > 0) {
        params.append('bestSeason', filters.bestSeason.join(','));
      }
      
      if (filters.difficulty && filters.difficulty !== 'any') {
        params.append('difficulty', filters.difficulty);
      }
      
      if (filters.crowdLevel && filters.crowdLevel !== 'any') {
        params.append('crowdLevel', filters.crowdLevel);
      }
      
      if (filters.typicalTimeSpent && filters.typicalTimeSpent !== 'any') {
        params.append('typicalTimeSpent', filters.typicalTimeSpent);
      }
      
      if (filters.selectedAttractionTags && filters.selectedAttractionTags.length > 0) {
        params.append('attractionTags', filters.selectedAttractionTags.join(','));
      }

      // Location-based search parameters
      if (filters.latitude !== undefined && filters.longitude !== undefined) {
        params.append('latitude', filters.latitude.toString());
        params.append('longitude', filters.longitude.toString());
        if (filters.radiusKm !== undefined) {
          params.append('radiusKm', filters.radiusKm.toString());
        }
      }

      const response = await fetch(`${this.baseUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch places');
      }

      return data.data;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  /**
   * Get a single place by ID
   * @param placeId - The place ID
   * @returns Promise with place data
   */
  async getPlaceById(placeId: string): Promise<PlaceItem> {
    try {
      const response = await fetch(`${this.baseUrl}/${placeId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch place');
      }

      return data.data;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }

  /**
   * Get total count of places by type
   * @param type - The type to count
   * @returns Promise with count data
   */
  async getPlacesCount(type: string): Promise<{ count: number; type: string }> {
    try {
      const params = new URLSearchParams();
      params.append('type', type);

      const response = await fetch(`${this.baseUrl}/count?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get places count');
      }

      return data.data;

    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error. Please check your connection.');
    }
  }
}

export const exploreService = new ExploreService();
